import {
  FloatingFocusManager,
  autoUpdate,
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import React from 'react';
import { greys, mainColors } from '../../../styling/theme';
import {
  AdminPanelSettingsOutlined,
  HistoryOutlined,
  LogoutOutlined,
  ManageAccountsOutlined,
} from '@mui/icons-material';
import { logOut } from '../../../redux/auth/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { userIdSelector } from '../../../redux/auth/selectors';
import makeStyles from '@mui/styles/makeStyles';
import TokenService from '../../../services/token-service';
import { isExternalAdministrator } from '../../../utilities/userRoles';

const useStyles = makeStyles({
  button: {
    height: '2.50rem',
    width: '2.50rem',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: mainColors.mainBlue,
    fontSize: '0.88rem',
    fontWeight: 600,
    border: `1px solid ${mainColors.mainBlue}`,
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: greys.grey100,
    lineHeight: '2.5rem',
    textAlign: 'center',
    '&:hover': {
      boxShadow: `0 0 0 3px ${greys.grey200}`,
    },
  },
  menu: {
    marginTop: '0.31rem',
    display: 'flex',
    flexDirection: 'column',
    width: '18.75rem',
    backgroundColor: 'white',
    borderRadius: '0.25rem',
    color: mainColors.mainBlue,
    border: `2px solid ${mainColors.mainBlue}`,
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '0.25rem',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: mainColors.lightGrey,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: mainColors.mainBlue_lighter,
      borderRadius: '0.62rem',
    },
  },
  menuHeader: {
    display: 'flex',
    padding: '0.62rem',
  },
  menuHeaderIcon: {
    height: '2.50rem',
    width: '2.50rem',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: mainColors.mainBlue,
    fontSize: '0.88rem',
    fontWeight: 600,
    border: `1px solid ${mainColors.mainBlue}`,
    userSelect: 'none',
    backgroundColor: greys.grey100,
    lineHeight: '2.5rem',
    textAlign: 'center',
  },
  menuHeaderContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    marginLeft: '0.62rem',
  },
  menuHeaderName: {
    fontSize: '1.00rem',
    fontWeight: 600,
  },
  menuHeaderAction: {
    fontSize: '0.69rem',
    fontWeight: 500,
    color: mainColors.mainBlue_lightAlt,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      color: mainColors.mainBlue_lighter,
    },
  },
  divider: {
    width: '100%',
    borderTop: `1px solid ${mainColors.mainBlue}`,
  },
  menuItem: {
    display: 'flex',
    padding: '0.62rem',
    fontSize: '0.88rem',
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
    gap: '0.62rem',
    '&:hover': {
      backgroundColor: greys.grey100,
    },
  },
  menuItemIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& svg': {
      fontSize: '1.25rem',
    },
  },
});

const UserOptionsMenu: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userName = useSelector(userIdSelector);

  const [isOpen, setIsOpen] = React.useState(false);

  const userDetails = TokenService.getUser();
  const canManageOrganization = isExternalAdministrator(userDetails?.user_type);

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-end',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleLogOut = () => {
    dispatch(logOut());
    navigate('/raptor/login', { replace: true });
  };

  const handleGoToMyAccount = () => {
    setIsOpen(false);
    navigate(`/settings/my-account`);
  };

  const handleGoToManageAccounts = () => {
    setIsOpen(false);
    navigate(`/settings/manage-accounts`);
  };

  const handleGoToSecuritySettings = () => {
    setIsOpen(false);
    navigate(`/settings/security-settings`);
  };

  const handleGoToAuditLog = () => {
    setIsOpen(false);
    navigate(`/settings/audit-log`);
  };

  return (
    <>
      <div
        ref={refs.setReference}
        role="button"
        tabIndex={0}
        {...getReferenceProps()}
        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-mainblue bg-gray-100 font-bold text-mainblue transition-all duration-200 hover:bg-blue-50 hover:shadow-md"
      >
        {userName && userName.length ? userName[0].toUpperCase() : ''}
      </div>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className={classes.menu}
          >
            <div className={classes.menuHeader}>
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-mainblue bg-gray-100 font-bold text-mainblue">
                {userName && userName.length ? userName[0].toUpperCase() : ''}
              </div>
              <div className={classes.menuHeaderContent}>
                <div className={classes.menuHeaderName}>{userName}</div>
                <div
                  className={classes.menuHeaderAction}
                  onClick={handleGoToMyAccount}
                >
                  My Account Settings
                </div>
              </div>
            </div>
            <div className={classes.divider}></div>
            {canManageOrganization ? (
              <div
                className={classes.menuItem}
                onClick={handleGoToManageAccounts}
              >
                <div className={classes.menuItemIcon}>
                  <ManageAccountsOutlined />
                </div>
                <div>Manage Accounts</div>
              </div>
            ) : null}
            {canManageOrganization ? (
              <div
                className={classes.menuItem}
                onClick={handleGoToSecuritySettings}
              >
                <div className={classes.menuItemIcon}>
                  <AdminPanelSettingsOutlined />
                </div>
                <div>Security Settings</div>
              </div>
            ) : null}
            {canManageOrganization ? (
              <div className={classes.menuItem} onClick={handleGoToAuditLog}>
                <div className={classes.menuItemIcon}>
                  <HistoryOutlined />
                </div>
                <div>Audit Log</div>
              </div>
            ) : null}
            <div className={classes.divider}></div>
            <div className={classes.menuItem} onClick={handleLogOut}>
              <div className={classes.menuItemIcon}>
                <LogoutOutlined />
              </div>
              <div>Logout</div>
            </div>
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
};

export default UserOptionsMenu;
